import { useBankProfile } from '@contexts/BankProfileContext';
import { useAuth } from '@hooks/useAuth';
import type { RoleKey } from '@pages/settings/profile/utils';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import type { Role } from 'types/roles';

interface AssignmentDetails {
	type: RoleKey;
	phone_number: string;
	email: string;
}

export interface PersonalDataResponse {
	role: Role;
	username: string;
	customer_name: string;
	phone_number: string;
	email: string;
	assignments: AssignmentDetails[];
}

export interface PersonalData {
	role: Role;
	username: string;
	customer_name: string;
	phone_number: string;
	email: string;
	assignments: Record<RoleKey, AssignmentDetails | undefined>;
}

export const usePersonalData = () => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();

	const fetchPersonalData = async (): Promise<PersonalDataResponse> => {
		const response = await axios.get(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile.id}/user-details`,
			{
				headers: { Authorization: session?.tokenized },
			},
		);

		return response.data;
	};

	return useQuery({
		queryKey: ['personal-data', selectedProfile?.id],
		queryFn: fetchPersonalData,
		enabled: !!selectedProfile?.id,
		select: (data): PersonalData => {
			const result: PersonalData = {
				...data,
				assignments: {
					user: undefined,
					signatory: undefined,
					ubo: undefined,
					director: undefined,
					shareholder: undefined,
				},
			};

			data.assignments.forEach((user: AssignmentDetails) => {
				result.assignments[user.type] = user;
			});

			return result;
		},
		refetchOnWindowFocus: false,
	});
};
